/**
 * Executive Insights — Phase 5
 *
 * Renders ONLY for role === 'executive' (super-visibility role).
 *
 * The Executive sees a stacked composite of the other dashboards:
 *   1. Final Approval summary (Final Approved / Pending Final)
 *   2. GTM Sales Leader view  -> reuses GTMSalesLeaderInsights
 *   3. CX Leader view         -> reuses CXLeaderInsights
 *   4. BFR view               -> reuses BFRInsights
 *
 * Each child component manages its own data fetching, so this page is a
 * thin orchestrator — no backend duplication.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Crown, ShieldCheck, Hourglass, Loader2, RefreshCw,
} from 'lucide-react';
import GTMSalesLeaderInsights from './GTMSalesLeaderInsights';
import CXLeaderInsights from './CXLeaderInsights';
import BFRInsights from './BFRInsights';
import DrilldownModal, { ClickableCard } from './DrilldownModal';
import OffboardingInsightsCards from './OffboardingInsightsCards';
import ExecutiveCommandCenter from './ExecutiveCommandCenter';

const Section = ({ title, accent, children, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    <div className="flex items-center gap-2 px-1">
      <span className={`w-1.5 h-5 rounded-sm ${accent}`} />
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
    </div>
    {children}
  </div>
);

const ExecutiveInsights = () => {
  const [topData, setTopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/executive`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setTopData(await res.json());
    } catch { /* keep UI rendering even if top summary fails */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const execCards = topData?.executive_cards || {};

  // Phase 17 — show the old department-merged view collapsed by default
  // so the new Command Center owns the operational signal at first glance.
  const [showDeepDives, setShowDeepDives] = useState(false);

  return (
    <div className="space-y-8" data-testid="executive-insights">
      <div className="flex items-center justify-between bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl shadow-sm border border-amber-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
            <Crown size={22} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Executive Insights</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Operations Command Center — what&apos;s pending, overdue, blocked, and where to act.
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-amber-200 text-gray-700 bg-white rounded-lg hover:bg-amber-50 disabled:opacity-50"
          data-testid="executive-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Phase 17 — NEW operations command center pinned to the top. */}
      <ExecutiveCommandCenter />

      {/* Existing department deep-dives — preserved, collapsed-by-default. */}
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={() => setShowDeepDives((v) => !v)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50"
          data-testid="executive-deepdives-toggle"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">
              Department Deep Dives
            </span>
            <span className="text-xs text-gray-400">(GTM · CX · BFR · Offboarding · Final Approval)</span>
          </div>
          <span className="text-xs font-medium text-teal-600">
            {showDeepDives ? 'Hide' : 'Show'}
          </span>
        </button>
      </div>

      {showDeepDives && (
        <>
      <Section title="Final Approval Status" accent="bg-amber-500" testId="executive-section-final">
        {loading && !topData ? (
          <div className="flex justify-center py-8 text-gray-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="executive-cards-grid">
            <ClickableCard
              label="Final Approved (Verified)"
              value={execCards.final_approved ?? 0}
              Icon={ShieldCheck}
              accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }}
              testId="executive-card-approved"
              onClick={() => setDrilldown({ card: 'executive_approved', title: 'Verified Accounts' })}
            />
            <ClickableCard
              label="Pending Final Approval"
              value={execCards.pending_final ?? 0}
              Icon={Hourglass}
              accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}
              testId="executive-card-pending"
              onClick={() => setDrilldown({ card: 'executive_pending', title: 'Pending Final Approval' })}
            />
          </div>
        )}
      </Section>

      <Section title="GTM Sales Leader View" accent="bg-teal-500" testId="executive-section-gtm">
        <GTMSalesLeaderInsights hideOffboardingCards />
      </Section>

      <Section title="CX Leader View" accent="bg-indigo-500" testId="executive-section-cx">
        <CXLeaderInsights hideOffboardingCards />
      </Section>

      <Section title="BFR View" accent="bg-rose-500" testId="executive-section-bfr">
        <BFRInsights hideOffboardingCards />
      </Section>

      {/* Phase 16 — Offboarding cards at executive scope (all 6 actionable steps). */}
      <Section title="Offboarding Overview" accent="bg-amber-500" testId="executive-section-offboarding">
        <OffboardingInsightsCards />
      </Section>
        </>
      )}

      {drilldown && (
        <DrilldownModal
          title={drilldown.title}
          card={drilldown.card}
          scope="global"
          onClose={() => setDrilldown(null)}
          rowAction={{
            label: 'Open',
            onClick: (row) => {
              setDrilldown(null);
              window.location.assign(`/leads/${row.lead_id}`);
            },
          }}
        />
      )}
    </div>
  );
};

export default ExecutiveInsights;
